/**
 * DR Cart Gifts Reconciler
 * 
 * Race-condition-free gift management for Focal cart drawer.
 * All cart mutations flow through a single promise queue.
 * Renders only from the final reconciled state.
 */
(function() {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════════════
  // DEBUG MODE
  // Append ?drGiftsDebug to URL to enable logging
  // ═══════════════════════════════════════════════════════════════════════════
  
  const DEBUG = new URLSearchParams(location.search).has('drGiftsDebug');

  function debugLog(...args) {
    if (DEBUG) console.log('[DR Gifts]', ...args);
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PROMISE QUEUE - Serializes all cart mutations
  // ═══════════════════════════════════════════════════════════════════════════
  
  class PromiseQueue {
    constructor() {
      this._queue = Promise.resolve();
      this._pending = 0;
    }

    get isBusy() {
      return this._pending > 0;
    }

    enqueue(fn) {
      this._pending++;
      updateButtonStates(); // ✅ Disable immediately when task starts
      
      // Create the task: swallow previous errors so new work can always run
      const task = this._queue
        .catch(() => {}) // Swallow previous errors - chain always recovers
        .then(() => fn());
      
      // Internal queue always resolves (errors handled per-task, not chain-wide)
      this._queue = task.catch(() => {});
      
      // Return the actual task so callers see their own errors
      return task
        .catch(err => {
          console.error('[DR Gifts] Queue error:', err);
          throw err;
        })
        .finally(() => {
          this._pending--;
          updateButtonStates(); // ✅ Re-enable when done
        });
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EXTERNAL MUTATION LOCK
  // Covers the window during theme.js fetches BEFORE reconciler queue is engaged
  // ═══════════════════════════════════════════════════════════════════════════
  
  let externalPending = 0;
  let externalIdleResolvers = [];

  /**
   * Call when starting an external mutation (e.g., /cart/add.js, /cart/change.js)
   * Returns a function to call when the mutation is complete
   */
  function beginExternalMutation() {
    externalPending++;
    updateButtonStates();
    let ended = false;

    return function endExternalMutation() {
      if (ended) return; // Prevent double-end
      ended = true;
      externalPending = Math.max(0, externalPending - 1);
      updateButtonStates();
      
      // Resolve any waiting whenIdle() calls
      if (externalPending === 0) {
        const resolvers = externalIdleResolvers.splice(0);
        resolvers.forEach(r => r());
      }
    };
  }

  /**
   * Wait for both reconciler queue AND external mutations to complete
   * Used by checkout gate to ensure cart is fully settled
   * Uses a loop to handle edge cases where new tasks enqueue during drain
   */
  async function whenIdle() {
    while (true) {
      const q1 = queue._queue;
      await q1;

      if (externalPending > 0) {
        await new Promise(resolve => externalIdleResolvers.push(resolve));
        continue; // Loop to drain queue again after external settles
      }

      const q2 = queue._queue;
      await q2;

      // Only exit if nothing changed during our awaits
      if (q2 === queue._queue && externalPending === 0) return;
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CHECKOUT ARM LOCK
  // Blocks ALL cart mutations while checkout validation is in progress
  // ═══════════════════════════════════════════════════════════════════════════
  
  let checkoutArmed = false;

  function armCheckout() {
    debugLog('armCheckout()');
    checkoutArmed = true;
    updateButtonStates();
  }

  function disarmCheckout() {
    debugLog('disarmCheckout()');
    checkoutArmed = false;
    updateButtonStates();
  }

  /**
   * Check if system is busy (checkout armed OR queue OR external mutations)
   */
  function isSystemBusy() {
    return checkoutArmed || queue.isBusy || externalPending > 0;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MEMBERS BLOCK REOPEN
  // When a loyalty coupon is applied/created, the mini-cart section re-renders
  // and destroys the open <details> element. This flag lets the drawer observer
  // re-open it on the very next childList mutation (before paint).
  // ═══════════════════════════════════════════════════════════════════════════

  let membersReopenPending = false;
  let cartRevision = 0;

  function nextCartRevision() {
    cartRevision += 1;
    return cartRevision;
  }

  function requestMembersReopen() {
    membersReopenPending = true;
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BFCACHE HANDLER
  // Reset locks on page restore to prevent stuck state.
  // Mobile browsers aggressively use bfcache, restoring JS state including locks.
  // Without this, cart mutations get blocked after navigating back from checkout.
  // ═══════════════════════════════════════════════════════════════════════════

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return; // Only handle bfcache restores
    
    debugLog('Page restored from bfcache - resetting locks');
    
    // Reset external mutation counter (lost callbacks won't resolve)
    if (externalPending > 0) {
      debugLog(`Clearing stuck externalPending: ${externalPending}`);
      externalPending = 0;
      // Resolve any waiting whenIdle() calls
      const resolvers = externalIdleResolvers.splice(0);
      resolvers.forEach(r => r());
    }
    
    // Reset checkout armed (user is back, not checking out anymore)
    if (checkoutArmed) {
      debugLog('Clearing stuck checkoutArmed');
      checkoutArmed = false;
    }
    
    // Update UI to reflect unlocked state
    updateButtonStates();
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // CAPTURE-PHASE MUTATION GUARDS
  // Hard-blocks cart mutation links when system is busy
  // Covers: click, Enter key, Space key activation
  // Scoped to cart drawer only
  // ═══════════════════════════════════════════════════════════════════════════

  const MUTATION_LINK_SELECTOR = 'a[href^="/cart/change"], a[href^="/cart/update"], a[href^="/cart/clear"]';

  function blockMutationEvent(e) {
    if (!isSystemBusy()) return;
    
    // Only guard inside cart drawer
    const drawer = e.target.closest('cart-drawer, #mini-cart');
    if (!drawer) return;
    
    // Check if interacting with a cart mutation link
    const link = e.target.closest(MUTATION_LINK_SELECTOR);
    if (!link) return;
    
    // Block the event
    debugLog('BLOCKED cart mutation (system busy):', e.type, link.href);
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();
  }

  // Click guard (capture phase)
  document.addEventListener('click', blockMutationEvent, true);

  // Keydown guard - anchors can be activated via Enter/Space
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    blockMutationEvent(e);
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // CONFIG MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  
  let cachedConfig = null;
  let warnedNoConfig = false;

  /**
   * Normalize raw config object into standard format
   * Returns frozen object to prevent accidental mutation
   */
  function normalizeConfig(parsed) {
    if (!parsed) return null;
    const free = (parsed.free || []).filter(Boolean).map(Number);
    const bonusVariantId = Number(parsed.bonus?.variantId) || 0;
    const bonus2VariantId = Number(parsed.bonus2?.variantId) || 0;
    const modules = parsed.modules || {};
    return Object.freeze({
      free: Object.freeze(free),
      bonus: Object.freeze({
        variantId: bonusVariantId,
        threshold: Number(parsed.bonus?.threshold) || 80000,
        useDiscountedTotal: parsed.bonus?.useDiscountedTotal !== false
      }),
      bonus2: Object.freeze({
        variantId: bonus2VariantId,
        threshold: Number(parsed.bonus2?.threshold) || 120000,
        useDiscountedTotal: parsed.bonus2?.useDiscountedTotal !== false
      }),
      modules: Object.freeze({
        freePicker: modules.freePicker !== false && free.length > 0,
        bonus1: modules.bonus1 !== false && bonusVariantId > 0,
        bonus2: modules.bonus2 !== false && bonus2VariantId > 0
      }),
      defaultFreeIndex: Number(parsed.defaultFreeIndex) || 0,
      sections: Object.freeze(parsed.sections || ['mini-cart']),
      sectionsUrl: parsed.sectionsUrl || '/cart'
    });
  }

  function hasActiveGiftModules(config) {
    return !!(
      config?.modules?.freePicker ||
      config?.modules?.bonus1 ||
      config?.modules?.bonus2
    );
  }

  /**
   * Get config from global window.DR_GIFTS_CONFIG (set in theme.liquid head)
   * Warns loudly if config is missing to catch "half-working" scenarios
   */
  function getConfig() {
    if (!cachedConfig && window.DR_GIFTS_CONFIG) {
      cachedConfig = normalizeConfig(window.DR_GIFTS_CONFIG);
    }
    if (!cachedConfig && !warnedNoConfig) {
      warnedNoConfig = true;
      console.warn('[DR Gifts] Missing window.DR_GIFTS_CONFIG (config snippet not loaded?)');
    }
    return cachedConfig;
  }
  
  /**
   * Check if object looks like a real cart (not just /cart/add.js response)
   */
  function isValidCartObject(obj) {
    return obj &&
      typeof obj === 'object' &&
      Array.isArray(obj.items) &&
      typeof obj.total_price === 'number' &&
      typeof obj.item_count === 'number';
  }

  /**
   * Build Set of all gift variant IDs for O(1) lookup
   */
  function getAllGiftVariantIds(config) {
    const ids = new Set(config.free);
    if (config.bonus.variantId) {
      ids.add(config.bonus.variantId);
    }
    if (config.bonus2?.variantId) {
      ids.add(config.bonus2.variantId);
    }
    return ids;
  }

  /**
   * Build Set of gift variant IDs currently allowed by module toggles.
   * Free gifts are included only when free picker is ON.
   */
  function getAllowedGiftVariantIds(config) {
    const ids = new Set();
    if (config.modules?.freePicker) {
      for (const variantId of (config.free || [])) {
        ids.add(variantId);
      }
    }
    if (config.modules?.bonus1 && config.bonus?.variantId) {
      ids.add(config.bonus.variantId);
    }
    if (config.modules?.bonus2 && config.bonus2?.variantId) {
      ids.add(config.bonus2.variantId);
    }
    return ids;
  }

  /**
   * Compute eligible total for threshold calculations:
   * - If useDiscountedTotal: uses cart.total_price (respects ALL discounts including order-level coupons)
   * - Otherwise: uses original_line_price sum (pre-discount, ignores coupons)
   * - Gift items have 0 price so they don't affect the total in either mode
   */
  function computeEligibleTotal(cart, config, allGiftIds) {
    const useDiscountedTotal =
      config.bonus.useDiscountedTotal !== false ||
      config.bonus2?.useDiscountedTotal !== false;
    if (useDiscountedTotal) {
      // New approach: use cart.total_price (respects all discounts)
      return Number(cart.total_price) || 0;
    } else {
      // Old approach: sum original_line_price, excluding gifts
      return (cart.items || []).reduce((sum, item) => {
        if (allGiftIds.has(item.variant_id)) return sum;
        return sum + (Number(item.original_line_price) || 0);
      }, 0);
    }
  }

  /**
   * Pure function: Compute desired gift state from cart + config
   * @param {Object} cart - Valid cart object with items array
   * @param {Object} config - Normalized config object
   * @param {Set<number>} allGiftIds - Set of all gift variant IDs (free + bonus)
   * @param {Set<number>} freeSet - Set of free gift variant IDs only
   * @param {number|null} overrideFreeGift - Force specific free gift (for selectFreeGift)
   * @returns {{ desiredFree: number|null, desiredBonusQty: number, desiredBonus2Qty: number }}
   */
  function computeDesired(cart, config, allGiftIds, freeSet, overrideFreeGift = null) {
    const freeModuleEnabled = !!config.modules?.freePicker && freeSet.size > 0;
    const bonusModuleEnabled = !!config.modules?.bonus1 && !!config.bonus.variantId;
    const bonus2ModuleEnabled = !!config.modules?.bonus2 && !!config.bonus2?.variantId;

    // Check for paid items (non-gift items)
    const hasPaid = cart.items.some(i => !allGiftIds.has(i.variant_id));

    if (!hasPaid) {
      // No paid items - no gifts allowed (removes all gifts from gift-only cart)
      return { desiredFree: null, desiredBonusQty: 0, desiredBonus2Qty: 0 };
    }

    let desiredFree = null;
    if (freeModuleEnabled) {
      // Determine desired free gift
      const freeInCart = cart.items.filter(i => freeSet.has(i.variant_id));
      if (overrideFreeGift && freeSet.has(overrideFreeGift)) {
        // Explicit selection via selectFreeGift()
        desiredFree = overrideFreeGift;
      } else if (freeInCart.length === 1) {
        // Exactly one free gift exists - keep it
        desiredFree = freeInCart[0].variant_id;
      } else {
        // 0 or >1 free gifts - force default
        desiredFree = config.free[config.defaultFreeIndex] || config.free[0] || null;
      }
    }

    // Compute eligible total based on config setting
    const eligibleTotal = computeEligibleTotal(cart, config, allGiftIds);

    const desiredBonusQty = bonusModuleEnabled && eligibleTotal >= config.bonus.threshold ? 1 : 0;
    const desiredBonus2Qty = bonus2ModuleEnabled && eligibleTotal >= config.bonus2.threshold ? 1 : 0;

    return { desiredFree, desiredBonusQty, desiredBonus2Qty };
  }

  /**
   * Dispatch cart events with consistent payload shape
   * Ensures cart.sections is always an object (never undefined)
   * @param {Object} cart - Cart object (sections will be normalized to {})
   */
  function dispatchCartEvents(cart, opts = {}) {
    const safeCart = cart || {};
    safeCart.sections = safeCart.sections || {};
    const detail = { cart: safeCart, revision: nextCartRevision() };
    if (opts.source) {
      detail.source = String(opts.source);
    }
    if (typeof opts.openMiniCart === 'boolean') {
      detail.openMiniCart = opts.openMiniCart;
    }
    if (Number.isFinite(opts.replacementDelay)) {
      detail.replacementDelay = opts.replacementDelay;
    }
    
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail
    }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail
    }));
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // CART UTILITIES
  // ═══════════════════════════════════════════════════════════════════════════
  
  async function fetchCart() {
    const response = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // RECONCILIATION LOGIC
  // ═══════════════════════════════════════════════════════════════════════════
  
  const queue = new PromiseQueue();

  /**
   * Core reconciliation function
   * @param {Object} opts
   * @param {Object} opts.cart - Cart JSON (optional, will fetch if not provided)
   * @param {Array} opts.sections - Sections to request (default: ['mini-cart'])
   * @param {string} opts.sectionsUrl - URL context for section rendering
   * @param {number} opts.desiredFreeGift - Force specific free gift variant ID (for selectFreeGift)
   * @returns {Promise<{cart: Object, sections: Object}>}
   */
  async function _reconcile(opts = {}) {
    const config = getConfig();
    
    // CRITICAL: Preserve sections from original response BEFORE upgrading cart
    // /cart/add.js returns sections but is not a valid cart object
    // /cart.js returns a valid cart but has NO sections
    // We must not lose the sections when upgrading!
    const existingSections = opts.cart?.sections;
    
    if (!config || !hasActiveGiftModules(config)) {
      // No active gift modules - ensure we have a real cart object
      let cart = opts.cart;
      if (!isValidCartObject(cart)) {
        cart = await fetchCart();
        // Re-attach sections from original response
        if (existingSections) {
          cart.sections = existingSections;
        }
      }
      return { cart, sections: cart.sections || existingSections || {} };
    }

    // Build Sets ONCE at the start (avoid recomputing 3x per reconcile)
    const allGiftIds = getAllGiftVariantIds(config);
    const freeSet = new Set(config.free);
    
    // 1. Get cart data - ensure it's a valid cart object (not just /cart/add.js response)
    let cart = opts.cart;
    if (!isValidCartObject(cart)) {
      cart = await fetchCart();
      // Re-attach sections from original response (critical for Focal drawer)
      if (existingSections) {
        cart.sections = existingSections;
      }
    }
    
    // 2. Compute desired state using pure function (shared Sets passed in)
    const { desiredFree, desiredBonusQty, desiredBonus2Qty } = computeDesired(
      cart, config, allGiftIds, freeSet, opts.desiredFreeGift
    );
    
    // 3. Identify current line quantities for diffing
    const freeGiftLines = cart.items.filter(item => freeSet.has(item.variant_id));
    const bonusLine = config.bonus.variantId 
      ? cart.items.find(item => item.variant_id === config.bonus.variantId)
      : null;
    const bonus2Line = config.bonus2?.variantId
      ? cart.items.find(item => item.variant_id === config.bonus2.variantId)
      : null;
    
    // 4. Build updates object (diff current vs desired)
    const updates = {};
    let needsUpdate = false;
    
    // Free gifts - set desired to 1, all others to 0
    // When desiredFree is null (gift-only cart), ALL free gifts get qty 0
    for (const variantId of config.free) {
      const currentQty = freeGiftLines.find(l => l.variant_id === variantId)?.quantity || 0;
      const desiredQty = (variantId === desiredFree) ? 1 : 0;
      
      if (currentQty !== desiredQty) {
        updates[variantId] = desiredQty;
        needsUpdate = true;
      }
    }
    
    // Bonus gift - only diff if bonus is enabled (variantId is truthy)
    if (config.bonus.variantId) {
      const currentBonusQty = bonusLine?.quantity || 0;
      if (currentBonusQty !== desiredBonusQty) {
        updates[config.bonus.variantId] = desiredBonusQty;
        needsUpdate = true;
      }
    }
    if (config.bonus2?.variantId) {
      const currentBonus2Qty = bonus2Line?.quantity || 0;
      if (currentBonus2Qty !== desiredBonus2Qty) {
        updates[config.bonus2.variantId] = desiredBonus2Qty;
        needsUpdate = true;
      }
    }
    
    // Stale/duplicate gifts - remove any gift-like item that's not part of
    // the currently allowed module set.
    // Catches old gifts from recovered abandoned checkouts.
    const allowedGiftIds = getAllowedGiftVariantIds(config);
    for (const item of cart.items) {
      const title = String(item.product_title || item.title || '');
      const isGiftLike = allGiftIds.has(item.variant_id) || title.includes('מתנה');
      if (!isGiftLike) continue;
      if (allowedGiftIds.has(item.variant_id)) continue;

      if ((item.quantity || 0) > 0 && updates[item.variant_id] === undefined) {
        updates[item.variant_id] = 0;
        needsUpdate = true;
        debugLog('Removing stale gift:', item.variant_id, title);
      }
    }

    // 5. Apply updates if needed
    if (needsUpdate) {
      const sectionsUrl = opts.sectionsUrl || window.location.pathname + window.location.search;
      const sections = opts.sections || config.sections || ['mini-cart'];
      
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          updates,
          sections,
          sections_url: sectionsUrl
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update cart');
      }
      
      const updatedCart = await response.json();
      
      // Panic invariant check (debug mode only)
      if (DEBUG) {
        runPanicChecks(updatedCart, config, allGiftIds);
      }
      
      return {
        cart: updatedCart,
        sections: updatedCart.sections || {}
      };
    }
    
    // No changes needed - return existing cart + sections
    // Use existingSections as fallback (preserved from /cart/add.js response)
    
    // Panic invariant check (debug mode only)
    if (DEBUG) {
      runPanicChecks(cart, config, allGiftIds);
    }
    
    return {
      cart,
      sections: cart.sections || existingSections || {}
    };
  }
  
  /**
   * Debug-only panic checks to catch invalid cart states
   */
  function runPanicChecks(cart, config, allGiftIds) {
    const eligibleTotal = computeEligibleTotal(cart, config, allGiftIds);
    const hasBonus = cart.items.some(i => i.variant_id === config.bonus.variantId);
    const shouldHaveBonus = config.modules?.bonus1 && config.bonus.variantId && eligibleTotal >= config.bonus.threshold;
    const hasBonus2 = cart.items.some(i => i.variant_id === config.bonus2?.variantId);
    const shouldHaveBonus2 = config.modules?.bonus2 && config.bonus2?.variantId && eligibleTotal >= config.bonus2.threshold;
    
    if (hasBonus !== shouldHaveBonus) {
      console.warn('[DR Gifts] PANIC: Bonus state mismatch after reconcile!', {
        hasBonus,
        shouldHaveBonus,
        eligibleTotal,
        threshold: config.bonus.threshold,
        cartSnapshot: JSON.stringify(cart.items.map(i => ({ id: i.variant_id, qty: i.quantity })))
      });
    }
    if (hasBonus2 !== shouldHaveBonus2) {
      console.warn('[DR Gifts] PANIC: Bonus tier-2 state mismatch after reconcile!', {
        hasBonus2,
        shouldHaveBonus2,
        eligibleTotal,
        threshold2: config.bonus2.threshold,
        cartSnapshot: JSON.stringify(cart.items.map(i => ({ id: i.variant_id, qty: i.quantity })))
      });
    }
    
    const hasPaid = cart.items.some(i => !allGiftIds.has(i.variant_id));
    const hasAnyGift = cart.items.some(i => allGiftIds.has(i.variant_id));
    if (!hasPaid && hasAnyGift) {
      console.warn('[DR Gifts] PANIC: Gift present without paid items!', {
        items: cart.items.map(i => i.variant_id)
      });
    }
  }

  /**
   * Public API: Ensure cart is reconciled
   * Queued to prevent race conditions
   */
  function ensureReconciled(opts = {}) {
    return queue.enqueue(() => _reconcile(opts));
  }

  /**
   * Public API: Select a specific free gift
   * Queued and uses _reconcile internally
   */
  function selectFreeGift(variantId) {
    const numericId = Number(variantId);
    if (!numericId) return Promise.resolve();
    const config = getConfig();
    if (!config?.modules?.freePicker || config.free.length === 0) return Promise.resolve();
    
    return queue.enqueue(async () => {
      const result = await _reconcile({ desiredFreeGift: numericId });
      
      // Build cart with sections for event dispatch
      const cartForEvents = result.cart;
      cartForEvents.sections = result.sections || cartForEvents.sections || {};
      
      // Dispatch events using helper (ensures consistent payload shape)
      // Note: no openMiniCart - drawer is already open during gift selection
      dispatchCartEvents(cartForEvents, { source: 'reconciler:select-free-gift' });
      
      return result;
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // BUTTON STATE MANAGEMENT
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Helper: Set anchor disabled state while preserving original tabIndex
   */
  function setAnchorDisabled(el, disabled) {
    el.setAttribute('aria-disabled', String(disabled));
    el.style.pointerEvents = disabled ? 'none' : '';

    if (disabled) {
      // Store original tabIndex before overwriting
      if (!el.hasAttribute('data-dr-prev-tabindex')) {
        el.setAttribute('data-dr-prev-tabindex', el.getAttribute('tabindex') ?? '');
      }
      el.setAttribute('tabindex', '-1');
    } else {
      // Restore original tabIndex
      const prev = el.getAttribute('data-dr-prev-tabindex');
      el.removeAttribute('data-dr-prev-tabindex');
      if (prev === '' || prev === null) {
        el.removeAttribute('tabindex');
      } else {
        el.setAttribute('tabindex', prev);
      }
    }
  }

  function updateButtonStates() {
    const busy = isSystemBusy();
    
    // 1. Disable gift selection buttons (these ARE buttons)
    document.querySelectorAll('[data-dr-gift-select]').forEach(btn => {
      btn.disabled = busy;
      btn.setAttribute('aria-busy', String(busy));
    });
    
    // 2. Disable checkout controls (buttons + anchors) - DRAWER + CART PAGE
    const checkoutSelector = [
      // Mini-cart drawer
      'cart-drawer button[name="checkout"]',
      'cart-drawer button.checkout-button',
      'cart-drawer a[href*="/checkout"]',
      '#mini-cart button[name="checkout"]',
      '#mini-cart button.checkout-button',
      '#mini-cart a[href*="/checkout"]',
      // Cart page
      'form.cart__recap button[name="checkout"]',
      '.cart__checkout-button'
    ].join(', ');
    
    document.querySelectorAll(checkoutSelector).forEach(el => {
      if (el.tagName === 'BUTTON') {
        el.disabled = busy;
        el.setAttribute('aria-disabled', String(busy));
      } else {
        setAnchorDisabled(el, busy);
      }
    });
    
    // 3. Disable quantity/remove controls (ANCHORS - cannot use disabled!)
    const mutationSelector = [
      // Umbrella: any cart mutation link in drawer
      'cart-drawer a[href^="/cart/change"]',
      'cart-drawer a[href^="/cart/update"]',
      '#mini-cart a[href^="/cart/change"]',
      '#mini-cart a[href^="/cart/update"]'
    ].join(', ');
    
    document.querySelectorAll(mutationSelector).forEach(el => {
      setAnchorDisabled(el, busy);
    });
    
    // 4. Disable quantity inputs
    const inputSelector = [
      'cart-drawer input[name="updates[]"]',
      '#mini-cart input[name="updates[]"]'
    ].join(', ');
    
    document.querySelectorAll(inputSelector).forEach(el => {
      el.disabled = busy;
      el.setAttribute('aria-disabled', String(busy));
    });

    // 5. Disable coupon remove pills while queue/locks are active
    document.querySelectorAll('[data-dr-coupon-remove]').forEach(btn => {
      if (btn.tagName === 'BUTTON') {
        btn.disabled = busy;
      }
      btn.setAttribute('aria-disabled', String(busy));
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // EVENT DELEGATION - Gift selection clicks
  // ═══════════════════════════════════════════════════════════════════════════
  
  document.addEventListener('click', (e) => {
    const giftBtn = e.target.closest('[data-dr-gift-select]');
    if (!giftBtn) return;
    
    // Parse as number (data attribute comes as string)
    const variantId = Number(giftBtn.dataset.drGiftSelect);
    if (!variantId) return;
    
    e.preventDefault();
    selectFreeGift(variantId);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // CHECKOUT GATE - Prevent checkout with invalid cart state
  // ═══════════════════════════════════════════════════════════════════════════
  
  /**
   * Hard gate for checkout - validates cart state before allowing checkout
   * Handles both form submit and direct checkout link clicks
   */
  /**
   * Validate free gift invariant:
   * - If cart has paid items: exactly one configured free gift (qty 1)
   * - If cart has no paid items: no free gifts
   * @returns {boolean} true if valid, false if invalid
   */
  function validateFreeGiftState(cart, config, allGiftIds) {
    const freeSet = new Set(config.free);
    const freeModuleEnabled = !!config.modules?.freePicker && freeSet.size > 0;
    const hasPaid = cart.items.some(i => !allGiftIds.has(i.variant_id));
    const freeInCart = cart.items.filter(i => freeSet.has(i.variant_id));

    if (!freeModuleEnabled) {
      // Picker disabled: free gifts must not exist.
      return freeInCart.length === 0;
    }
    
    if (hasPaid) {
      // Should have exactly 1 free gift with qty 1
      if (freeInCart.length !== 1) return false;
      if (freeInCart[0].quantity !== 1) return false;
    } else {
      // Should have no free gifts
      if (freeInCart.length > 0) return false;
    }
    return true;
  }

  /**
   * Validate that all gift-like items belong to currently allowed modules.
   * Gift-like = configured gift variant OR title includes "מתנה".
   */
  function validateAllowedGiftSet(cart, config, allGiftIds) {
    const allowedGiftIds = getAllowedGiftVariantIds(config);
    for (const item of (cart.items || [])) {
      const title = String(item.product_title || item.title || '');
      const isGiftLike = allGiftIds.has(item.variant_id) || title.includes('מתנה');
      if (!isGiftLike) continue;
      if (!allowedGiftIds.has(item.variant_id)) {
        return false;
      }
    }
    return true;
  }

  async function hardGateCheckout(e, triggerEl) {
    e.preventDefault();
    e.stopPropagation();
    if (e.stopImmediatePropagation) e.stopImmediatePropagation();

    // CRITICAL: Arm checkout IMMEDIATELY (sync) before any async work
    // This disables all cart mutation controls via updateButtonStates()
    // AND enables the capture-phase click guard
    armCheckout();
    
    const config = getConfig();
    if (!config) {
      // No gift config - allow checkout
      proceedToCheckout(triggerEl);
      return;
    }
    
    try {
      // Wait for ALL pending work (queue AND external mutations)
      await whenIdle();
      
      // ALWAYS validate using a FRESH server cart (not stale event payload)
      let cart = await fetchCart();
      const allGiftIds = getAllGiftVariantIds(config);
      
      // Check 1: Must have paid items (or empty cart)
      const hasPaid = cart.items.some(i => !allGiftIds.has(i.variant_id));
      if (!hasPaid && cart.items.length > 0) {
        debugLog('Checkout BLOCKED: cart contains only gift items');
        disarmCheckout();
        return; // Block checkout
      }
      
      // Check 2: Only currently allowed modules may have gifts in cart
      if (!validateAllowedGiftSet(cart, config, allGiftIds)) {
        debugLog('Disallowed gift detected, reconciling...');
        await ensureReconciled({ cart });

        // Wait again and re-fetch to confirm fix
        await whenIdle();
        cart = await fetchCart();

        if (!validateAllowedGiftSet(cart, config, allGiftIds)) {
          debugLog('Checkout BLOCKED: disallowed gift still present after retry');
          disarmCheckout();
          return; // Block checkout (fail-closed)
        }
      }

      // Check 3: Free gift state must be valid
      // Invariant: exactly 1 free gift when paid items exist, 0 otherwise
      if (!validateFreeGiftState(cart, config, allGiftIds)) {
        debugLog('Free gift mismatch detected, reconciling...');
        await ensureReconciled({ cart });
        
        // Wait again and re-fetch to confirm fix
        await whenIdle();
        cart = await fetchCart();
        
        if (!validateFreeGiftState(cart, config, allGiftIds)) {
          debugLog('Checkout BLOCKED: free gift state invalid after retry');
          disarmCheckout();
          return; // Block checkout (fail-closed)
        }
      }
      
      // Check 4: Bonus gift tiers must match thresholds
      const bonusChecks = [
        { key: 'tier1', enabled: !!config.modules?.bonus1, variantId: config.bonus?.variantId, threshold: config.bonus?.threshold },
        { key: 'tier2', enabled: !!config.modules?.bonus2, variantId: config.bonus2?.variantId, threshold: config.bonus2?.threshold }
      ].filter(check => !!check.variantId);
      for (const bonusCheck of bonusChecks) {
        if (!bonusCheck.enabled) continue;
        const hasBonus = cart.items.some(i => i.variant_id === bonusCheck.variantId);
        const eligibleTotal = computeEligibleTotal(cart, config, allGiftIds);
        const shouldHaveBonus = eligibleTotal >= bonusCheck.threshold;
        
        if (hasBonus !== shouldHaveBonus) {
          // Mismatch! Reconcile with fresh cart and re-check (use queue to serialize)
          debugLog('Bonus mismatch detected, reconciling...', {
            tier: bonusCheck.key,
            hasBonus,
            shouldHaveBonus,
            eligibleTotal
          });
          await ensureReconciled({ cart });
          
          // Wait again and re-fetch
          await whenIdle();
          const cart2 = await fetchCart();
          const hasBonus2 = cart2.items.some(i => i.variant_id === bonusCheck.variantId);
          const eligibleTotal2 = computeEligibleTotal(cart2, config, allGiftIds);
          const shouldHaveBonus2 = eligibleTotal2 >= bonusCheck.threshold;
          
          if (hasBonus2 !== shouldHaveBonus2) {
            debugLog('Checkout BLOCKED: bonus gift state invalid after retry', { tier: bonusCheck.key });
            disarmCheckout();
            return; // Block checkout
          }
        }
      }
      
      // All checks passed - proceed
      debugLog('Checkout validated, proceeding...');
      proceedToCheckout(triggerEl);
      
    } catch (err) {
      // Block on error - don't let invalid state through
      console.error('[DR Gifts] Checkout blocked due to error:', err);
      disarmCheckout();
    }
  }
  
  function proceedToCheckout(triggerEl) {
    // Support both mini-cart drawer form and cart page form
    const form = triggerEl?.closest?.('form#mini-cart-form, form.cart__recap');
    if (form) {
      form.dataset.drAllowSubmit = '1';
      form.submit();
    } else {
      window.location.href = '/checkout';
    }
  }
  
  // Submit gate (form submission) - covers BOTH mini-cart drawer AND cart page
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('form#mini-cart-form, form.cart__recap');
    if (!form) return;
    
    // If flag set, allow native submit (prevents recursion)
    if (form.dataset.drAllowSubmit === '1') {
      form.dataset.drAllowSubmit = '';
      return;
    }
    
    hardGateCheckout(e, form);
  }, true);
  
  // Click gate (for <a href="/checkout"> or button clicks) - covers BOTH mini-cart drawer AND cart page
  document.addEventListener('click', (e) => {
    const el = e.target.closest([
      // Mini-cart drawer
      'cart-drawer a[href*="/checkout"]',
      '#mini-cart a[href*="/checkout"]',
      'cart-drawer button[name="checkout"]',
      '#mini-cart button[name="checkout"]',
      // Cart page
      'form.cart__recap button[name="checkout"]',
      '.cart__checkout-button'
    ].join(', '));
    if (!el) return;
    
    // Don't double-handle if this is a form submit button (handled by submit listener)
    if (el.tagName === 'BUTTON' && el.type === 'submit' && el.closest('form')) return;
    
    hardGateCheckout(e, el);
  }, true);

  // ═══════════════════════════════════════════════════════════════════════════
  // DRAWER OPEN RECONCILIATION
  // ═══════════════════════════════════════════════════════════════════════════
  
  // Reconcile when cart drawer opens; re-open members block after section re-render.
  const observer = new MutationObserver((mutations) => {
    let hasChildListChange = false;

    for (const mutation of mutations) {
      if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
        const drawer = mutation.target;
        if (drawer.hasAttribute('open')) {
          ensureReconciled().catch(err => {
            console.warn('[DR Gifts] Reconciliation on drawer open failed:', err);
          });
        }
      }
      if (mutation.type === 'childList') {
        hasChildListChange = true;
      }
    }

    if (hasChildListChange && membersReopenPending) {
      membersReopenPending = false;
      const drawer = document.querySelector('cart-drawer, #mini-cart');
      const details = drawer && drawer.querySelector('[data-dr-members]');
      if (details instanceof HTMLDetailsElement && !details.open) {
        details.open = true;
        const loadingEl = details.querySelector('[data-dr-members-state="loading"]');
        if (loadingEl) loadingEl.removeAttribute('hidden');
      }
    }
  });

  function setupDrawerObserver() {
    const drawer = document.querySelector('cart-drawer, #mini-cart');
    if (drawer) {
      observer.observe(drawer, {
        attributes: true,
        attributeFilter: ['open'],
        childList: true,
        subtree: true
      });
    }
  }

  // Setup on DOMContentLoaded and after potential drawer re-renders
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupDrawerObserver);
  } else {
    setupDrawerObserver();
  }

  // Re-setup observer after cart:refresh (drawer HTML gets replaced)
  document.addEventListener('cart:refresh', () => {
    // Small delay to ensure DOM is updated
    requestAnimationFrame(setupDrawerObserver);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // DISCOUNT CODE MANAGEMENT
  // Applies discount codes via Cart API (single-writer, queued)
  // ═══════════════════════════════════════════════════════════════════════════

  // SessionStorage key for tracking codes we've applied (best-effort preservation)
  const APPLIED_CODES_KEY = 'drReconciler.appliedCodes';

  /**
   * Get list of codes we've applied via our UI (sessionStorage)
   * @returns {string[]}
   */
  function getOurAppliedCodes() {
    try {
      const raw = sessionStorage.getItem(APPLIED_CODES_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Save a code to our "applied via UI" list (sessionStorage)
   */
  function saveOurAppliedCode(code) {
    try {
      const codes = getOurAppliedCodes();
      if (!codes.includes(code.toUpperCase())) {
        codes.push(code.toUpperCase());
        sessionStorage.setItem(APPLIED_CODES_KEY, JSON.stringify(codes));
      }
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Remove a code from our "applied via UI" list (best-effort)
   */
  function removeOurAppliedCode(code) {
    try {
      const target = String(code || '').trim().toUpperCase();
      if (!target) return;
      const codes = getOurAppliedCodes().filter(c => String(c).toUpperCase() !== target);
      sessionStorage.setItem(APPLIED_CODES_KEY, JSON.stringify(codes));
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Extract discount codes from cart (best-effort)
   * Checks cart_level_discount_applications and line-level discounts
   * Optionally includes codes we've applied via our UI (sessionStorage)
   * @param {Object} cart - Cart object
   * @param {Object} [opts]
   * @param {boolean} [opts.includeSessionStorage=true]
   * @returns {string[]} - Array of uppercase discount codes
   */
  function extractDiscountCodes(cart, opts = {}) {
    const includeSessionStorage = opts.includeSessionStorage !== false;
    const codes = new Set();
    const codePattern = /^[A-Z0-9_-]{2,20}$/i;
    
    // Helper to add code if it looks valid
    function addIfCode(str) {
      if (str && typeof str === 'string' && codePattern.test(str.trim())) {
        codes.add(str.trim().toUpperCase());
      }
    }
    
    // 1. Cart-level discount applications
    const cartLevel = cart?.cart_level_discount_applications || [];
    for (const d of cartLevel) {
      if (d.code) addIfCode(d.code);
      else if (d.title) addIfCode(d.title);
    }
    
    // 2. Line-level discounts (Shopify uses multiple field names)
    for (const item of (cart?.items || [])) {
      // 2a. 'discounts' array - flat structure: {amount, title}
      // This is the primary field Shopify uses in Cart API responses
      for (const d of (item.discounts || [])) {
        if (d.code) addIfCode(d.code);
        else if (d.title) addIfCode(d.title);
      }
      
      // 2b. 'line_level_discount_allocations' - nested structure
      for (const alloc of (item.line_level_discount_allocations || [])) {
        const app = alloc.discount_application || alloc;
        if (app.code) addIfCode(app.code);
        else if (app.title) addIfCode(app.title);
      }
      
      // 2c. 'discount_allocations' - fallback (Liquid uses this name)
      for (const alloc of (item.discount_allocations || [])) {
        const app = alloc.discount_application || alloc;
        if (app.code) addIfCode(app.code);
        else if (app.title) addIfCode(app.title);
      }
    }
    
    // 3. Include codes we've applied via our UI (in case Shopify doesn't expose them)
    if (includeSessionStorage) {
      for (const code of getOurAppliedCodes()) {
        codes.add(code);
      }
    }
    
    return Array.from(codes);
  }

  /**
   * Check if a specific discount code is active in cart
   * Uses token-ish matching to avoid false positives (e.g., DR46 vs DR460)
   * @param {Object} cart - Cart object
   * @param {string} code - Discount code to check
   * @returns {boolean}
   */
  function isDiscountCodeActive(cart, code) {
    const CODE = String(code).trim().toUpperCase();
    const tokenRe = new RegExp('(^|[^A-Z0-9])' + CODE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '([^A-Z0-9]|$)', 'i');
    
    // Helper to check if a string matches our code
    function matchesCode(str) {
      if (!str) return false;
      const upper = String(str).toUpperCase();
      // Exact match or token match (avoid DR46 matching DR460)
      return upper === CODE || tokenRe.test(str);
    }
    
    // 1. Check cart-level discount applications
    const cartLevel = cart?.cart_level_discount_applications || [];
    for (const d of cartLevel) {
      if (matchesCode(d.code) || matchesCode(d.title)) return true;
    }
    
    // 2. Check line-level discounts (Shopify uses multiple field names)
    for (const item of (cart?.items || [])) {
      // 2a. 'discounts' array - flat structure: {amount, title}
      // This is the PRIMARY field Shopify uses in Cart API responses
      for (const d of (item.discounts || [])) {
        if (matchesCode(d.code) || matchesCode(d.title)) return true;
      }
      
      // 2b. 'line_level_discount_allocations' - nested structure
      for (const alloc of (item.line_level_discount_allocations || [])) {
        const app = alloc.discount_application || alloc;
        if (matchesCode(app.code) || matchesCode(app.title)) return true;
      }
      
      // 2c. 'discount_allocations' - fallback (Liquid uses this name)
      for (const alloc of (item.discount_allocations || [])) {
        const app = alloc.discount_application || alloc;
        if (matchesCode(app.code) || matchesCode(app.title)) return true;
      }
    }
    
    return false;
  }

  /**
   * Dedupe array (case-insensitive for discount codes)
   */
  function dedupeCodesArray(arr) {
    const seen = new Set();
    const result = [];
    for (const item of arr) {
      const upper = String(item).toUpperCase();
      if (!seen.has(upper)) {
        seen.add(upper);
        result.push(upper);
      }
    }
    return result;
  }

  /**
   * Apply a discount code via Cart API
   * Queued through the single-writer queue (consistent with gifts)
   * 
   * @param {string} code - Discount code to apply
   * @param {Object} opts - Options
   * @param {'append'|'replace'} opts.mode - 'append' preserves existing codes, 'replace' sets only this code
   * @returns {Promise<{success: boolean, cart: Object}>}
   */
  function applyDiscountCode(code, opts = {}) {
    const mode = opts.mode || 'append';
    const requestedCode = String(code).trim().toUpperCase();
    
    if (!requestedCode) {
      return Promise.resolve({ success: false, cart: null });
    }
    
    return queue.enqueue(async () => {
      debugLog('applyDiscountCode() start:', requestedCode, { mode });
      
      // 1. Fetch fresh cart to learn current state
      const beforeCart = await fetchCart();
      const beforeDiscountCount = (beforeCart.cart_level_discount_applications || []).length;
      
      // 2. Extract existing codes (best-effort)
      // Avoid sessionStorage here so old removed codes aren't re-applied
      const existingCodes = extractDiscountCodes(beforeCart, { includeSessionStorage: false });
      debugLog('Existing discount codes:', existingCodes);
      
      // 3. Build new discount string based on mode
      let discountString;
      if (mode === 'replace') {
        discountString = requestedCode;
      } else {
        // append + dedupe
        const nextCodes = dedupeCodesArray([...existingCodes, requestedCode]);
        discountString = nextCodes.join(',');
      }
      
      debugLog('Applying discount string:', discountString);
      
      // 4. Apply via Cart API with bundled sections
      const sectionsUrl = window.location.pathname + window.location.search;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: discountString,
          sections: ['mini-cart'],
          sections_url: sectionsUrl
        })
      });
      
      if (!response.ok) {
        console.error('[DR Gifts] applyDiscountCode failed:', response.status);
        return { success: false, cart: beforeCart };
      }
      
      let updatedCart = await response.json();
      
      // Save to our applied codes list (for future preservation)
      saveOurAppliedCode(requestedCode);
      
      // 5. Reconcile gifts immediately (ensures final state is settled)
      const reconcileResult = await _reconcile({
        cart: updatedCart,
        sections: ['mini-cart'],
        sectionsUrl
      });
      
      const finalCart = reconcileResult.cart;
      finalCart.sections = reconcileResult.sections || finalCart.sections || {};
      
      // 6. Post-condition validation
      const codeIsActive = isDiscountCodeActive(finalCart, requestedCode);
      const afterDiscountCount = (finalCart.cart_level_discount_applications || []).length;
      
      if (!codeIsActive) {
        debugLog('WARNING: Requested code not active after apply:', requestedCode);
      }
      
      if (DEBUG && beforeDiscountCount > 0 && afterDiscountCount < beforeDiscountCount) {
        console.warn('[DR Gifts] WARNING: Discount count decreased after apply', {
          before: beforeDiscountCount,
          after: afterDiscountCount,
          existingCodes,
          requestedCode
        });
      }
      
      // 7. Dispatch events once (consistent payload shape)
      dispatchCartEvents(finalCart, {
        source: 'reconciler:apply-discount',
        openMiniCart: true
      });
      
      debugLog('applyDiscountCode() complete, success:', codeIsActive);
      
      return { success: codeIsActive, cart: finalCart };
    });
  }

  /**
   * Remove a discount code via Cart API while preserving others
   * Queued through the single-writer queue (consistent with gifts/apply flow)
   *
   * @param {string} code - Discount code to remove
   * @returns {Promise<{success: boolean, cart: Object}>}
   */
  function removeDiscountCode(code) {
    const requestedCode = String(code).trim().toUpperCase();
    if (!requestedCode) {
      return Promise.resolve({ success: false, cart: null });
    }

    return queue.enqueue(async () => {
      debugLog('removeDiscountCode() start:', requestedCode);

      // 1. Fetch fresh cart to learn current state
      const beforeCart = await fetchCart();
      const existingCodes = extractDiscountCodes(beforeCart, { includeSessionStorage: false });
      const nextCodes = existingCodes.filter(c => String(c).toUpperCase() !== requestedCode);

      // Code not active, nothing to remove
      if (nextCodes.length === existingCodes.length) {
        return { success: true, cart: beforeCart };
      }

      // 2. Apply new discount list (or empty to clear all)
      const discountString = nextCodes.join(',');
      debugLog('Removing code, applying discount string:', discountString);

      const sectionsUrl = window.location.pathname + window.location.search;
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          discount: discountString,
          sections: ['mini-cart'],
          sections_url: sectionsUrl
        })
      });

      if (!response.ok) {
        console.error('[DR Gifts] removeDiscountCode failed:', response.status);
        return { success: false, cart: beforeCart };
      }

      let updatedCart = await response.json();
      removeOurAppliedCode(requestedCode);

      // 3. Reconcile gifts immediately for final settled cart state
      const reconcileResult = await _reconcile({
        cart: updatedCart,
        sections: ['mini-cart'],
        sectionsUrl
      });

      const finalCart = reconcileResult.cart;
      finalCart.sections = reconcileResult.sections || finalCart.sections || {};

      // 4. Post-condition validation
      const removed = !isDiscountCodeActive(finalCart, requestedCode);
      if (!removed) {
        debugLog('WARNING: Requested code still active after remove:', requestedCode);
      }

      // 5. Dispatch events to refresh drawer section
      dispatchCartEvents(finalCart, {
        source: 'reconciler:remove-discount',
        openMiniCart: true
      });

      debugLog('removeDiscountCode() complete, success:', removed);
      return { success: removed, cart: finalCart };
    });
  }

  // Coupon removal click handling (event delegation for re-rendered drawer content)
  document.addEventListener('click', (e) => {
    const removeBtn = e.target.closest('[data-dr-coupon-remove]');
    if (!removeBtn) return;

    const code = String(removeBtn.getAttribute('data-dr-coupon-remove') || '').trim();
    if (!code) return;

    e.preventDefault();
    e.stopPropagation();
    removeDiscountCode(code).catch(err => {
      console.error('[DR Gifts] Coupon remove failed:', err);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // PUBLIC API
  // ═══════════════════════════════════════════════════════════════════════════
  
  window.drGiftsReconciler = {
    ensureReconciled,
    selectFreeGift,
    applyDiscountCode,
    removeDiscountCode,
    isDiscountCodeActive,
    beginExternalMutation,
    whenIdle,
    armCheckout,
    disarmCheckout,
    requestMembersReopen,
    getNextCartRevision: nextCartRevision,
    get isBusy() { return isSystemBusy(); },
    get isCheckoutArmed() { return checkoutArmed; },
    getConfig
  };

})();
